/**
 * Режимы ассистента, доступные РОЛИ пользователя (US-10 FR-2.3).
 *
 * Набор режимов решает бэк, а не фронт. Это не оформление, а требование
 * постановки: наблюдателю режим «Загрузка» не возвращается ВОВСЕ (Сценарий
 * 7), а оператору возвращается с `enabled: false`, пока Волна 3 не
 * поставлена. Захардкоженный список показал бы наблюдателю кнопку, которой он
 * не может воспользоваться, а оператору — работающую кнопку к нереализованному
 * режиму.
 *
 * Пока ответ не пришёл или бэк недоступен, показываем режимы «только чтение»
 * (`data`, `guide`, `note`): они доступны любой роли, поэтому лишнего не
 * покажут. Добавлять «Загрузку» в фолбэк нельзя — это и был бы обход FR-2.3.
 */
import { useEffect, useMemo, useState } from 'react';

import { ASSISTANT_MODE_LIST, type AssistantModeDef } from './modes';
import { fetchModes } from '../../requests/assistant.request';
import type { AssistantMode } from '../../types/assistant';

/** Режимы, безопасные для любой роли, — фолбэк до ответа бэка. */
const READ_ONLY_MODES: readonly AssistantMode[] = ['data', 'guide', 'note'];

export interface AssistantModeState {
  modes: readonly AssistantModeDef[];
  /** Приветствие от бэка — оно локализовано там же, где сообщения об отказах. */
  greeting: string | null;
}

export const useAssistantModes = (
  kato: number | null,
  lang: string,
  /** `false` — панель закрыта: не дёргаем бэк зря. */
  active: boolean,
): AssistantModeState => {
  const [allowed, setAllowed] = useState<AssistantMode[] | null>(null);
  const [disabled, setDisabled] = useState<AssistantMode[]>([]);
  const [greeting, setGreeting] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    fetchModes(kato, lang)
      .then((data) => {
        if (cancelled) return;
        setAllowed(data.modes.map((item) => item.mode));
        setDisabled(
          data.modes.filter((item) => !item.enabled).map((item) => item.mode),
        );
        setGreeting(data.greeting || null);
      })
      .catch(() => {
        // Бэк недоступен — остаёмся на режимах «только чтение». Ошибку в ленту
        // не пишем: она встанет туда при первой же реплике, а пустая панель с
        // красным баннером до единого вопроса пугает без нужды.
        if (!cancelled) setAllowed(null);
      });
    return () => {
      cancelled = true;
    };
  }, [kato, lang, active]);

  const modes = useMemo(() => {
    const ids = allowed ?? READ_ONLY_MODES;
    return ASSISTANT_MODE_LIST.filter((mode) => ids.includes(mode.id)).map(
      (mode) => ({ ...mode, enabled: !disabled.includes(mode.id) }),
    );
  }, [allowed, disabled]);

  return { modes, greeting };
};
